import { useEffect, type RefObject } from "react";
import { haptic } from "./haptics";
import type { TetrisEngine } from "./engine";

const TAP_MS = 240;
const TAP_DIST = 18;
const STEP_PX = 28;
const SWIPE_AXIS = 36;
const FLICK_DOWN_MS = 220;
const DOUBLE_TAP_MS = 300;

/**
 * Tahta üstünde kaydır / tap jestleri.
 * Sol-sağ: adım adım kaydır. Tap: dön. Aşağı: soft drop. Hızlı aşağı: hard drop. Yukarı: hold.
 */
export function useTetrisGestures(
  surfaceRef: RefObject<HTMLElement | null>,
  engineRef: RefObject<TetrisEngine | null>,
  keysRef: { current: { down: boolean } },
  enabled: boolean
): void {
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || !enabled) return;

    let pointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startAt = 0;
    let lastStepX = 0;
    let lastTapAt = 0;
    let dragged = false;

    const resetKeys = () => {
      keysRef.current.down = false;
    };

    const onDown = (event: PointerEvent) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startAt = performance.now();
      lastStepX = event.clientX;
      dragged = false;
      surface.setPointerCapture(event.pointerId);
    };

    const onMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return;
      const engine = engineRef.current;
      if (!engine) return;

      const dx = event.clientX - lastStepX;
      const dy = event.clientY - startY;
      if (Math.abs(event.clientX - startX) > TAP_DIST || Math.abs(dy) > TAP_DIST) {
        dragged = true;
      }

      if (Math.abs(dx) >= STEP_PX && Math.abs(dx) >= Math.abs(event.clientY - startY) * 0.45) {
        const steps = Math.trunc(dx / STEP_PX);
        if (steps !== 0) {
          const dir = steps > 0 ? 1 : -1;
          for (let i = 0; i < Math.abs(steps); i += 1) {
            engine.move(dir);
          }
          lastStepX = event.clientX;
          haptic("move");
        }
      }

      keysRef.current.down = dy > SWIPE_AXIS && dy >= Math.abs(event.clientX - startX);
    };

    const onUp = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return;
      pointerId = null;
      resetKeys();
      const engine = engineRef.current;
      if (!engine) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const dt = performance.now() - startAt;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!dragged && dt <= TAP_MS && absX <= TAP_DIST && absY <= TAP_DIST) {
        if (performance.now() - lastTapAt < DOUBLE_TAP_MS) {
          engine.hardDrop();
          lastTapAt = 0;
          return;
        }
        lastTapAt = performance.now();
        engine.rotate(1);
        return;
      }

      if (absY > absX && absY > SWIPE_AXIS) {
        if (dy < 0) {
          engine.holdPiece();
          return;
        }
        if (dt <= FLICK_DOWN_MS || dy > 90) {
          engine.hardDrop();
        }
      }
    };

    const onCancel = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return;
      pointerId = null;
      resetKeys();
    };

    const blockScroll = (event: TouchEvent) => {
      event.preventDefault();
    };

    surface.addEventListener("pointerdown", onDown);
    surface.addEventListener("pointermove", onMove);
    surface.addEventListener("pointerup", onUp);
    surface.addEventListener("pointercancel", onCancel);
    surface.addEventListener("touchmove", blockScroll, { passive: false });
    return () => {
      resetKeys();
      surface.removeEventListener("pointerdown", onDown);
      surface.removeEventListener("pointermove", onMove);
      surface.removeEventListener("pointerup", onUp);
      surface.removeEventListener("pointercancel", onCancel);
      surface.removeEventListener("touchmove", blockScroll);
    };
  }, [enabled, engineRef, keysRef, surfaceRef]);
}
